import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Typography } from '@mui/material';

import { MotionContainer, varBounceInLeft, varBounceOutRight } from '../animate';
import BoxClienteAtendimento from './BoxClienteAtendimento';
import BoxClienteFila from './BoxClienteFila';

import { useWSContext } from '../../context/WebSocketContext';
import notification from '../BrowserNotifications';

function ListClientesOnline({ nClientesOnline }) {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    nextClient,
    handleSendMessageWS,
    newMessage,
    setNewMessage,
    reconnectingClients,
    chatsEncerrados,
    chats,
    removeChat,
    marcarChatComoEncerrado,
  } = useWSContext();

  const clientesEmAtendimento = chats || [];
  const [selectedChat, setSelectedChat] = useState({});
  const notifiedClientRef = useRef(null);

  // ⭐ LIMPA AUTOMATICAMENTE CHATS ENCERRADOS
  useEffect(() => {
    const chatsAtivosIds = chats.map((chat) => chat.id);
    const chatsParaLimpar = chatsAtivosIds.filter((id) =>
      chatsEncerrados.includes(id)
    );

    if (chatsParaLimpar.length > 0) {
      console.warn(
        '[LIMPEZA AUTOMÁTICA] Removendo chats que já foram encerrados mas ainda estavam na lista:',
        chatsParaLimpar
      );
      chatsParaLimpar.forEach((chatId) => {
        removeChat(chatId);
      });
    }
  }, [chats, chatsEncerrados, removeChat]);

  const uniqueClientsInQueue = useMemo(() => {
    if (!nextClient?.clients?.length) {
      return [];
    }
    const clientMap = new Map();
    nextClient.clients.forEach((client) => {
      const uniqueKey = String(client.id || client.numSerie || client.name || '');
      if (uniqueKey) {
        clientMap.set(uniqueKey, client);
      }
    });
    return Array.from(clientMap.values());
  }, [nextClient]);

  const fecharChat = useCallback(
    (chatId) => {
      if (!chatId) {
        console.warn('Tentativa de fechar chat sem ID.');
        return;
      }
      handleSendMessageWS({ serviceType: 'disconnectCliente', id: chatId });
      marcarChatComoEncerrado(chatId);
      removeChat(chatId);
    },
    [handleSendMessageWS, removeChat, marcarChatComoEncerrado]
  );

  // Redireciona para Home se chat for encerrado enquanto estiver ativo e limpa selectedChat
  useEffect(() => {
    chatsEncerrados.forEach((chatId) => {
      localStorage.removeItem(chatId);
      if (selectedChat?.id === chatId) {
        console.log(`Chat ativo ${chatId} foi encerrado. Redirecionando para home.`);
        setSelectedChat({});
        setTimeout(() => {
          navigate('/app/home');
        }, 500);
      }
    });
  }, [chatsEncerrados, selectedChat, navigate]);

  // 🚨 REMOVIDO O USEEFFECT DE NAVEGAÇÃO AUTOMÁTICA PARA O CHAT!

  // Limpa selectedChat ao sair da tela de chat
  useEffect(() => {
    if (location.pathname !== '/app/chat' && selectedChat?.id) {
      setSelectedChat({});
    }
  }, [location.pathname]);

  useEffect(() => {
    nClientesOnline(nextClient?.count || 0);
    const clienteNaFila = nextClient?.clients?.[0];
    if (clienteNaFila && notifiedClientRef.current !== clienteNaFila.id) {
      notification({
        title: `TECNOZAP: ${clienteNaFila.name} aguarda atendimento.`,
        body: `Clientes na fila: ${nextClient.count}.`,
      });
      notifiedClientRef.current = clienteNaFila.id;
    }
    if (nextClient?.count === 0) {
      notifiedClientRef.current = null;
    }
  }, [nextClient, nClientesOnline]);

  useEffect(() => {
    if (newMessage) {
      setNewMessage(null);
    }
  }, [newMessage, setNewMessage]);

  return (
    <Box>
      {/* Clientes na Fila de Espera */}
      {uniqueClientsInQueue.length > 0
        ? uniqueClientsInQueue.map((cliente) => (
            <MotionContainer key={cliente.id} initial="initial" open>
              <motion.div variants={varBounceInLeft}>
                <BoxClienteFila
                  key={cliente.id}
                  cliente={cliente}
                  obterCliente={() =>
                    handleSendMessageWS({ serviceType: 'getUser' })
                  }
                />
              </motion.div>
            </MotionContainer>
          ))
        : clientesEmAtendimento.length === 0 && (
            <Box marginBottom={1}>
              <Typography variant="caption">Nenhum cliente na fila</Typography>
            </Box>
          )}

      {/* Clientes Em Atendimento */}
      <AnimatePresence>
        {clientesEmAtendimento.map((cliente) => (
          <MotionContainer key={cliente?.id} initial="initial" open exit="exit">
            <motion.div variants={varBounceInLeft} exit={varBounceOutRight}>
              <BoxClienteAtendimento
                key={cliente?.id}
                fecharChat={() => fecharChat(cliente?.id)}
                cliente={cliente}
                active={selectedChat?.id === cliente?.id}
                onClick={() => {
                  setSelectedChat(cliente);
                  navigate('/app/chat', { state: cliente });
                }}
                alertMessage={
                  newMessage?.id === cliente?.id &&
                  newMessage?.id !== selectedChat?.id
                }
                isReconnecting={reconnectingClients.has(cliente?.id)}
                isClosed={chatsEncerrados.includes(cliente?.id)}
              />
            </motion.div>
          </MotionContainer>
        ))}
      </AnimatePresence>
    </Box>
  );
}

export default ListClientesOnline;
